import { Component, Element, h, Host, Prop, State } from "@stencil/core";

@Component({
    tag: "ux-form-group",
    styleUrl: "form-group.scss",
    shadow: false
})
export class FormGroup {
    @Element() element!: HTMLUxFormGroupElement;
    private manageAllCheckbox: HTMLUxCheckboxElement;
    private manageAllValue = "manage-all-checkboxes";

    /**
     * Label text describing the form fields
     */
    @Prop() readonly label: string = "";

    /**
     * Additional info label for the input field
     */
    @Prop() readonly labelInfo: string = "";

    /**
     * Indicates if the form group is required
     */
    @Prop() readonly required: boolean = false;

    /**
     * Indicates if the form group has an error state
     */
    @Prop() readonly error: boolean = false;

    /**
     * Error message that is rendered if component has an error
     */
    @Prop() readonly errorMsg: string = "";

    /**
     * Utility classes to be assigned to the label element
     */
    @Prop() readonly labelClasses: string = "";

    /**
     * Name assigned to the form group
     */
    @Prop() readonly name: string = "";

    /**
     * If set, an additional checkbox will be displayed that manages
     * all the checkboxes present inside the form group
     */
    @Prop() readonly manageAllCheckboxes: string;

    /**
     * String of classes that will be assigned to the manage all checkbox wrapper, if present
     */
    @Prop() readonly manageAllWrapperClass: string = "";

    @State() manageAllChecked = false;

    private onManageAllChange = (e: CustomEvent) => {
        const checkboxes = Array.from(this.element.querySelectorAll("ux-checkbox")) as HTMLUxCheckboxElement[];
        checkboxes.forEach((checkbox: HTMLUxCheckboxElement) => {
            checkbox.setAttribute("checked", e.detail.checked);
        });
    };

    private handleManageAllCheckboxStatus = (e: CustomEvent) => {
        if (this.manageAllCheckboxes !== undefined) {
            const eTarget = e.target as HTMLUxCheckboxElement;
            if (eTarget === this.manageAllCheckbox) {
                return;
            }
            const checkboxes = Array.from(
                this.element.querySelectorAll(`ux-checkbox:not([value="${this.manageAllCheckboxes}"])`)
            ) as HTMLUxCheckboxElement[];
            let checkedCheckboxes = 0;
            checkboxes.forEach((checkbox: HTMLUxCheckboxElement) => {
                if (checkbox !== this.manageAllCheckbox && checkbox.checked) {
                    checkedCheckboxes++;
                }
            });
            if (checkedCheckboxes !== checkboxes.length - 1) {
                this.manageAllCheckbox.setAttribute("checked", "false");
            } else {
                this.manageAllCheckbox.setAttribute("checked", "true");
            }
        }
    };

    render() {
        const baseClass = "ux-form-group";
        const { label, labelInfo, required, error, errorMsg, labelClasses, manageAllCheckboxes } = this;
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--error`]: this.error
                }}
                onUxCheckboxChange={this.handleManageAllCheckboxStatus}
            >
                <fieldset class={`${baseClass}__fieldset`}>
                    <ux-input-label
                        labelClasses={labelClasses}
                        text={label}
                        error={error}
                        errorMsg={errorMsg}
                        required={required}
                        labelInfo={labelInfo}
                    />
                    <div class={`${baseClass}__wrapper`}>
                        {manageAllCheckboxes && (
                            <div class={this.manageAllWrapperClass}>
                                <ux-checkbox
                                    onUxCheckboxChange={this.onManageAllChange}
                                    label={manageAllCheckboxes}
                                    value={this.manageAllValue}
                                    // @ts-expect-error Check for typing
                                    ref={(el) => (this.manageAllCheckbox = el)}
                                    checked={this.manageAllChecked}
                                />
                            </div>
                        )}
                        <slot />
                    </div>
                </fieldset>
            </Host>
        );
    }
}
